import {
  defineCalculatorQuiz,
  type Answers,
  type CalculatorBanner,
  type CalculatorInterpretation,
  type CalculatorInterpretationCard,
  type CalculatorRecommendation,
  type CalculatorReferenceRow,
  type CalculatorResult,
  type CalculatorScale,
  type CalculatorValue,
} from "./_schema";
import { calculateVermeulen, type VermeulenResult } from "./calculators/vermeulen";
import {
  freeTStatusPgml,
  bioTStatusNgdl,
  pctFreeStatus,
  FT_RANGE_PGML,
  BIO_T_RANGE_NGDL,
  PCT_FREE_RANGE,
  type RangeStatus,
} from "./calculators/range-status";

/**
 * FREE TESTOSTERONE CALCULATOR -- the first calculator-kind quiz on the
 * platform. Ports the standalone Vermeulen calculator from
 * andrew-marsmen/TestosteroneCalculator into the Mars Men quiz widget.
 *
 * Three numeric inputs (Total T required, SHBG/Albumin optional with sensible
 * defaults), one deterministic computed result, no persona routing.
 *
 * Reference ranges are sourced from Jasuja et al. 2023 (broader adult male
 * cohort, 66-309 pg/mL). The accuracy disclaimer is mandatory -- this is a
 * marketing/educational tool, not a diagnostic.
 *
 * The result object also carries the data for the "What This Means" (interpretation)
 * and "Mars Men" (recommendation) pages so the three-page result flow renders
 * straight from one pure compute, no separate authoring layer.
 *
 * Copy + decision-logic parity: every string and threshold in this file is
 * verbatim from the doctor-reviewed source HTML in
 * `andrew-marsmen/TestosteroneCalculator/free-testosterone-calculator.html`.
 * Do not edit copy or thresholds here without updating the source repo first.
 */

const QUESTION_ORDER = ["total-t", "shbg", "albumin"] as const;

const SHBG_DEFAULT_NMOLL = 35;
const ALBUMIN_DEFAULT_GDL = 4.3;

const FT_OPTIMAL_PGML = 120;

const STATUS_COPY = {
  low: {
    label: "Below Normal",
    message:
      "Your free testosterone is below the normal reference range. This may explain symptoms like fatigue, low libido, or reduced strength. Consider confirming with a retest (morning draw, 7-10 AM) and discussing with your physician.",
  },
  normal: {
    label: "Normal Range",
    message:
      "Your free testosterone falls within the normal reference range for adult men. If you still have symptoms, consider other factors like sleep, stress, and nutrition.",
  },
  high: {
    label: "Above Normal",
    message:
      "Your free testosterone is above the typical reference range. This can occur with supplementation or naturally high production. Discuss with your physician if unexpected.",
  },
} as const;

function readNumber(answers: Answers, key: string): number | null {
  const v = answers[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}

function requireNumber(answers: Answers, key: string): number {
  const v = readNumber(answers, key);
  if (v === null) {
    throw new Error(`free-t.compute: required answer "${key}" is missing or invalid`);
  }
  return v;
}

function compute(answers: Answers): CalculatorResult {
  const totalTngdl = requireNumber(answers, "total-t");
  const shbgRaw = readNumber(answers, "shbg");
  const shbgProvided = shbgRaw !== null;
  const shbgNmolL = shbgRaw ?? SHBG_DEFAULT_NMOLL;
  const albuminGdl = readNumber(answers, "albumin") ?? ALBUMIN_DEFAULT_GDL;

  const v = calculateVermeulen({ totalTngdl, shbgNmolL, albuminGdl });
  const freeTStatus = freeTStatusPgml(v.freeTpgml);
  const bioTStatus = bioTStatusNgdl(v.bioTngdl);
  const pctFreeStatusKey = pctFreeStatus(v.pctFree);
  const statusCopy = STATUS_COPY[freeTStatus];

  const hero: CalculatorValue = {
    key: "free-t",
    label: "Free Testosterone",
    value: roundTo(v.freeTpgml, 1),
    unit: "pg/mL",
    altUnit: { value: roundTo(v.freeTngdl, 2), unit: "ng/dL" },
    status: freeTStatus,
    referenceRange: `${FT_RANGE_PGML.low}-${FT_RANGE_PGML.high} pg/mL`,
  };

  // NOTE (2026-07-13): the two secondary-metric display labels below were
  // expanded from "Bioavailable T" / "% Free" to their full forms to match the
  // growth mockup for the t-calc Results page. This is a display-label change
  // only (no value/threshold/medical-claim change) and the full form
  // "Bioavailable Testosterone" was already used verbatim in the `scales`
  // interpretation below, so parity is intact. Per the file-header rule, sync
  // the source HTML in andrew-marsmen/TestosteroneCalculator to these labels so
  // a future parity audit does not flag them as drift.
  const secondaryValues: CalculatorValue[] = [
    {
      key: "bio-t",
      label: "Bioavailable Testosterone",
      value: roundTo(v.bioTngdl, 0),
      unit: "ng/dL",
      status: bioTStatus,
      referenceRange: `${BIO_T_RANGE_NGDL.low}-${BIO_T_RANGE_NGDL.high} ng/dL`,
    },
    {
      key: "pct-free",
      label: "% Free Testosterone",
      value: roundTo(v.pctFree, 2),
      unit: "%",
      status: pctFreeStatusKey,
      referenceRange: `${PCT_FREE_RANGE.low.toFixed(1)}-${PCT_FREE_RANGE.high.toFixed(1)}%`,
    },
  ];

  const breakdown = {
    title: "Where your testosterone goes",
    segments: largestRemainderRound(
      [
        { key: "shbg", label: "SHBG-bound", pct: v.pctShbg },
        { key: "albumin", label: "Albumin-bound", pct: v.pctAlbumin },
        { key: "free", label: "Free", pct: v.pctFree },
      ],
      1,
    ),
  };

  const referenceTable: CalculatorReferenceRow[] = [
    {
      metric: "Free Testosterone",
      value: `${roundTo(v.freeTpgml, 1)} pg/mL`,
      range: `${FT_RANGE_PGML.low} - ${FT_RANGE_PGML.high} pg/mL`,
      status: freeTStatus,
    },
    {
      metric: "Bioavailable T",
      value: `${roundTo(v.bioTngdl, 0)} ng/dL`,
      range: `${BIO_T_RANGE_NGDL.low} - ${BIO_T_RANGE_NGDL.high} ng/dL`,
      status: bioTStatus,
    },
    {
      metric: "% Free",
      value: `${roundTo(v.pctFree, 2)}%`,
      range: `${PCT_FREE_RANGE.low.toFixed(1)} - ${PCT_FREE_RANGE.high.toFixed(1)}%`,
      status: pctFreeStatusKey,
    },
  ];

  const interpretation = buildInterpretation({
    vermeulen: v,
    totalTngdl,
    freeTStatus,
    bioTStatus,
    pctFreeStatusKey,
    shbgProvided,
  });

  const recommendation = buildRecommendation({
    freeTStatus,
    pctFreeStatusKey,
  });

  const banner: CalculatorBanner | undefined = shbgProvided
    ? undefined
    : {
        emoji: "ℹ️",
        title: "Estimate based on average SHBG (35 nmol/L).",
        body:
          "Your actual free testosterone could be higher or lower depending on your real SHBG level. For a more accurate result, ask your doctor to add SHBG to your next blood panel.",
      };

  return {
    hero,
    status: { key: freeTStatus, label: statusCopy.label, message: statusCopy.message },
    ...(banner ? { banner } : {}),
    secondaryValues,
    breakdown,
    referenceTable,
    interpretation,
    recommendation,
    methodology:
      "Calculated using the Vermeulen method (Vermeulen et al., 1999). Free testosterone reference ranges from Jasuja et al., 2023 (Andrology).",
    disclaimer:
      "This calculator is for educational purposes only and is not a substitute for medical advice.",
  };
}

function buildInterpretation(args: {
  vermeulen: VermeulenResult;
  totalTngdl: number;
  freeTStatus: RangeStatus;
  bioTStatus: RangeStatus;
  pctFreeStatusKey: RangeStatus;
  shbgProvided: boolean;
}): CalculatorInterpretation {
  const { vermeulen, totalTngdl, freeTStatus, bioTStatus, pctFreeStatusKey, shbgProvided } = args;

  const scales: CalculatorScale[] = [
    {
      key: "free-t",
      label: "Free Testosterone",
      value: roundTo(vermeulen.freeTpgml, 1),
      unit: "pg/mL",
      status: freeTStatus,
      min: 0,
      max: 400,
      lowMax: FT_RANGE_PGML.low,
      highMin: FT_RANGE_PGML.high,
      lowCopy: `Low (<${FT_RANGE_PGML.low})`,
      normalCopy: "Normal",
      highCopy: `High (>${FT_RANGE_PGML.high})`,
    },
    {
      key: "bio-t",
      label: "Bioavailable Testosterone",
      value: roundTo(vermeulen.bioTngdl, 1),
      unit: "ng/dL",
      status: bioTStatus,
      min: 0,
      max: 800,
      lowMax: BIO_T_RANGE_NGDL.low,
      highMin: BIO_T_RANGE_NGDL.high,
      lowCopy: `Low (<${BIO_T_RANGE_NGDL.low})`,
      normalCopy: "Normal",
      highCopy: `High (>${BIO_T_RANGE_NGDL.high})`,
    },
  ];

  const cards: CalculatorInterpretationCard[] = [];

  const pctShbgRounded = roundTo(vermeulen.pctShbg, 0);
  if (vermeulen.pctShbg > 70) {
    cards.push({
      key: "high-shbg",
      emoji: "🔒",
      title: "High SHBG Binding",
      body: `Over ${pctShbgRounded}% of your testosterone is locked up by SHBG. This is above typical levels and means less testosterone reaches your tissues. Factors like aging, low-calorie diets, or liver conditions can elevate SHBG.`,
      accent: "orange",
    });
  } else if (vermeulen.pctShbg < 50) {
    cards.push({
      key: "low-shbg",
      emoji: "🔓",
      title: "Lower SHBG Binding",
      body: `Only ${pctShbgRounded}% of your testosterone is SHBG-bound, which is below average. This can occur with obesity, insulin resistance, or certain medications. More of your testosterone is bioavailable.`,
      accent: "blue",
    });
  } else {
    cards.push({
      key: "typical-shbg",
      emoji: "⚖️",
      title: "Typical SHBG Binding",
      body: `${pctShbgRounded}% of your testosterone is SHBG-bound, which falls within the expected range. Your body is converting total testosterone into usable forms at a normal rate.`,
      accent: "green",
    });
  }

  const freeTRounded = roundTo(vermeulen.freeTpgml, 1);
  if (vermeulen.freeTpgml < FT_RANGE_PGML.low) {
    cards.push({
      key: "free-t-low",
      emoji: "⚠️",
      title: "Low Free Testosterone",
      body: `At ${freeTRounded} pg/mL, your free T is below the normal floor of ${FT_RANGE_PGML.low} pg/mL. This can cause fatigue, brain fog, low libido, reduced muscle mass, and difficulty recovering from exercise.`,
      accent: "red",
    });
  } else if (vermeulen.freeTpgml < FT_OPTIMAL_PGML) {
    cards.push({
      key: "free-t-lower-normal",
      emoji: "📊",
      title: "Lower-Normal Free T",
      body: `At ${freeTRounded} pg/mL, you're in the normal range but below the optimal threshold of ${FT_OPTIMAL_PGML} pg/mL for men 19-39. You may still experience subtle symptoms like reduced energy or slower recovery.`,
      accent: "orange",
    });
  } else {
    cards.push({
      key: "free-t-healthy",
      emoji: "💪",
      title: "Healthy Free Testosterone",
      body: `At ${freeTRounded} pg/mL, your free testosterone is solidly within the healthy range. This level supports energy, muscle protein synthesis, cognitive function, and libido.`,
      accent: "green",
    });
  }

  const pctFreeRounded = roundTo(vermeulen.pctFree, 2);
  if (pctFreeStatusKey === "low") {
    cards.push({
      key: "pct-free-low",
      emoji: "📉",
      title: `Low % Free (${pctFreeRounded}%)`,
      body: "Less than 2% of your total testosterone is free. Even if your total T looks good on paper, your body may not be able to use enough of it. This is a binding issue, not a production issue.",
      accent: "red",
    });
  } else {
    cards.push({
      key: "pct-free-healthy",
      emoji: "✅",
      title: `Healthy % Free (${pctFreeRounded}%)`,
      body:
        "Between 2-3% of total testosterone as free is the expected range. Your binding proteins are working normally, allowing an appropriate amount of testosterone to reach your cells.",
      accent: "green",
    });
  }

  cards.push(buildTotalTContextCard(totalTngdl));

  const nextSteps = buildNextSteps({
    freeTStatus,
    pctFreeStatusKey,
    shbgProvided,
  });

  return { scales, cards, nextSteps };
}

function buildTotalTContextCard(totalTngdl: number): CalculatorInterpretationCard {
  const totalRounded = Math.round(totalTngdl);
  if (totalTngdl < 300) {
    return {
      key: "total-t-context",
      emoji: "🔬",
      title: "Low Total Testosterone",
      body: `Your total testosterone of ${totalRounded} ng/dL is below 300 ng/dL, the commonly used clinical threshold. This suggests a production issue that may warrant further evaluation with your doctor.`,
      accent: "red",
    };
  }
  if (totalTngdl < 450) {
    return {
      key: "total-t-context",
      emoji: "🔍",
      title: "Borderline Total Testosterone",
      body: `Your total testosterone of ${totalRounded} ng/dL is above the clinical threshold but below the mid-range. Combined with your free T levels, this gives a more complete picture of your hormone status.`,
      accent: "orange",
    };
  }
  return {
    key: "total-t-context",
    emoji: "🎯",
    title: "Solid Total Testosterone",
    body: `Your total testosterone of ${totalRounded} ng/dL is in a healthy range. The key question is how much of it your body can actually use -- that's what free and bioavailable T tell you.`,
    accent: "green",
  };
}

function buildNextSteps(args: {
  freeTStatus: RangeStatus;
  pctFreeStatusKey: RangeStatus;
  shbgProvided: boolean;
}): CalculatorInterpretation["nextSteps"] {
  const { freeTStatus, pctFreeStatusKey, shbgProvided } = args;
  const items: { emoji: string; title: string; body: string }[] = [];

  // If SHBG was estimated, the doctor-reviewed original always prepends this
  // step (regardless of which branch fires below).
  if (!shbgProvided) {
    items.push({
      emoji: "🧪",
      title: "Get SHBG tested for accurate results",
      body: "Your result is based on the population average of 35 nmol/L. Real SHBG varies widely (10-57 in healthy men), which dramatically changes free T. Ask your doctor to add SHBG to your next panel -- it's usually a $20-30 add-on.",
    });
  }

  if (freeTStatus === "low") {
    items.push({
      emoji: "🩸",
      title: "Confirm with a retest",
      body: "Testosterone fluctuates daily. Get a morning blood draw (7-10 AM) after a good night's sleep to confirm.",
    });
    items.push({
      emoji: "🔎",
      title: "Identify the cause",
      body: "Is your total T low (production issue) or is SHBG high (binding issue)? The fix depends on which factor is driving the problem.",
    });
    items.push({
      emoji: "🛠️",
      title: "Address modifiable factors",
      body: "Prioritize sleep (7-9 hrs), manage stress, maintain healthy body composition, and ensure adequate zinc, vitamin D, and magnesium.",
    });
  } else if (pctFreeStatusKey === "low") {
    items.push({
      emoji: "🔎",
      title: "Investigate SHBG levels",
      body: "Your total T is reasonable but too much is bound. High SHBG can be driven by aging, low-calorie diets, liver conditions, or estrogen exposure.",
    });
    items.push({
      emoji: "💊",
      title: "Consider SHBG modulators",
      body: "Boron has been shown to reduce SHBG and increase free testosterone in as little as 7 days (Naghii et al., 2011).",
    });
    items.push({
      emoji: "🥩",
      title: "Optimize your diet",
      body: "Adequate protein and healthy fats support lower SHBG levels. Very low-calorie or low-fat diets can elevate SHBG.",
    });
  } else {
    items.push({
      emoji: "📅",
      title: "Retest periodically",
      body: "Even with healthy levels, tracking your free T over time helps you catch changes early -- especially after age 35 when SHBG tends to rise.",
    });
    items.push({
      emoji: "⚡",
      title: "Optimize performance",
      body: "With healthy free T, focus on maximizing its effects through resistance training, quality sleep, and stress management.",
    });
    items.push({
      emoji: "🧪",
      title: "Track the full panel",
      body: "Ask your doctor for total T, free T, SHBG, estradiol, and DHEA-S for a complete picture of your hormonal health.",
    });
  }

  return { title: "Recommended Next Steps", items };
}

function buildRecommendation(args: {
  freeTStatus: RangeStatus;
  pctFreeStatusKey: RangeStatus;
}): CalculatorRecommendation {
  const { freeTStatus, pctFreeStatusKey } = args;

  // Two-way split, verbatim from the doctor-reviewed original
  // (updateCTA: freeT_pgml<66 || pctFree<2.0 -> binding body; else generic).
  const body =
    freeTStatus === "low" || pctFreeStatusKey === "low"
      ? "Your free testosterone is low relative to your total -- that means SHBG is binding more than expected. Mars Men's Level 2 (Keep More) with Boron targets SHBG to help liberate bound testosterone."
      : "Mars Men was built to address all three bottlenecks in testosterone optimization -- production, binding, and utilization.";

  return {
    headline: "Optimize Your Free Testosterone",
    headlineAccent: "Free Testosterone",
    body,
    levels: [
      { eyebrow: "PATHWAY 01", title: "Make More T" },
      { eyebrow: "PATHWAY 02", title: "Keep More T" },
      { eyebrow: "PATHWAY 03", title: "Use T Better" },
    ],
    cta: {
      // Bare PDP -- no authored utm_*. This href used to carry
      // `?utm_source=quiz&utm_medium=calculator&utm_campaign=free-t`, which
      // overwrote the ad channel's attribution on every t-calc order and
      // forwarded none of the landing URL's click ids. `CalculatorResult`
      // now forwards the real attribution onto this href at render time
      // (web/src/utils/redirect.ts). Do not re-add UTMs here.
      label: "SHOP MARS MEN",
      href: "https://mengotomars.com/pages/boost-natural-testosterone-support",
    },
  };
}

/**
 * Structured derived-result payload for the t-calc Klaviyo wire (Issue 1).
 *
 * Mirrors `TCalcDerivedResult` in `api/schemas/submission.py`. Same input ->
 * same output as `compute(answers)` (parity per rule #11) -- the values here
 * are bit-for-bit the same numbers the user sees on the result page, just
 * narrowed to the five fields Klaviyo segments and flows need.
 *
 * Do NOT recompute. Surface only. The whole point is reproducibility from
 * persisted `answers` server-side; if a new field is added, run the same
 * `compute` internals.
 */
export interface TCalcDerivedResult {
  free_t_pgml: number;
  free_t_status: "low" | "normal" | "high";
  total_t_ngdl: number;
  shbg_provided: boolean;
  percent_free: number;
}

/**
 * Pure function from `answers` -> `TCalcDerivedResult`. Mirrors what
 * `compute(answers)` already calculates; we only expose the subset Klaviyo
 * branches on. Throws on the same invalid inputs that `compute` throws on
 * (missing required total-t, etc.) -- keeps callsites symmetric.
 */
export function computeDerived(answers: Answers): TCalcDerivedResult {
  const totalTngdl = requireNumber(answers, "total-t");
  const shbgRaw = readNumber(answers, "shbg");
  const shbgProvided = shbgRaw !== null;
  const shbgNmolL = shbgRaw ?? SHBG_DEFAULT_NMOLL;
  const albuminGdl = readNumber(answers, "albumin") ?? ALBUMIN_DEFAULT_GDL;

  const v = calculateVermeulen({ totalTngdl, shbgNmolL, albuminGdl });

  return {
    free_t_pgml: roundTo(v.freeTpgml, 1),
    free_t_status: freeTStatusPgml(v.freeTpgml),
    total_t_ngdl: totalTngdl,
    shbg_provided: shbgProvided,
    percent_free: roundTo(v.pctFree, 2),
  };
}

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * Largest-remainder rounding: round each segment's pct to `decimals` precision
 * such that the rounded values sum to 100. Prevents the breakdown bar from
 * adding to 99.9% / 100.1% after naive per-segment rounding.
 */
function largestRemainderRound<T extends { pct: number }>(segments: T[], decimals: number): T[] {
  const factor = 10 ** decimals;
  const scaled = segments.map((s) => s.pct * factor);
  const floored = scaled.map((x) => Math.floor(x));
  let remainder = Math.round(100 * factor) - floored.reduce((a, b) => a + b, 0);
  const fractionalOrder = scaled
    .map((x, idx) => ({ idx, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac);
  const increments = new Array<number>(segments.length).fill(0);
  for (let i = 0; i < remainder && i < fractionalOrder.length; i += 1) {
    increments[fractionalOrder[i]!.idx] = 1;
  }
  return segments.map((s, idx) => ({
    ...s,
    pct: (floored[idx]! + increments[idx]!) / factor,
  }));
}

export const freeTQuiz = defineCalculatorQuiz({
  id: "free-t",
  slug: "free-t",
  kind: "calculator",
  meta: {
    title: "Free Testosterone Calculator",
    introHeadline: "Free Testosterone Calculator",
    introBody:
      "Total testosterone doesn't tell the full story. Use the Vermeulen method to estimate your free and bioavailable testosterone from standard lab work.",
  },
  questions: [
    {
      id: "total-t",
      type: "number",
      prompt: "What's your total testosterone?",
      helper: "Your most recent lab result.",
      unit: "ng/dL",
      placeholder: "e.g. 500",
      min: 1,
      max: 2000,
      step: 1,
    },
    {
      id: "shbg",
      type: "number",
      prompt: "What's your SHBG?",
      helper: "Optional -- average for adult men is 35 nmol/L.",
      unit: "nmol/L",
      placeholder: "e.g. 35",
      min: 1,
      max: 500,
      step: 0.1,
      defaultValue: SHBG_DEFAULT_NMOLL,
      optional: true,
      whyWeAsk: {
        heading: "Why we ask",
        body: "SHBG binds testosterone tightly, leaving less free. Without your real SHBG, we use the population average of 35 nmol/L. Getting SHBG tested (~$30 add-on) dramatically improves accuracy.",
      },
    },
    {
      id: "albumin",
      type: "number",
      prompt: "What's your albumin?",
      helper: "Optional -- typical value is 4.3 g/dL.",
      unit: "g/dL",
      placeholder: "e.g. 4.3",
      min: 1,
      max: 10,
      step: 0.1,
      defaultValue: ALBUMIN_DEFAULT_GDL,
      optional: true,
    },
  ],
  branching: (_answers, currentId) => {
    const idx = QUESTION_ORDER.indexOf(currentId as (typeof QUESTION_ORDER)[number]);
    if (idx === -1) return null;
    return QUESTION_ORDER[idx + 1] ?? null;
  },
  compute,
});
